import React, { useState, useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  makeStyles,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Box,
} from "@material-ui/core";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import { createGradientStyles } from "../../utils/themeColors";

const useStyles = makeStyles((theme) => ({
  // ✅ Estilos modernos do modal seguindo padrão do projeto
  dialogTitle: {
    padding: theme.spacing(2, 3),
    flexShrink: 0,
    color: '#fff',
    '& .MuiTypography-root': {
      fontWeight: 600,
      fontSize: '1.2rem',
      color: 'inherit',
    }
  },
  ...createGradientStyles(theme),
  dialogContent: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#fafafa',
    overflowY: 'auto',
    overflowX: 'hidden',
    flex: '1 1 auto',
    maxHeight: 'calc(100vh - 250px)',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: theme.palette.type === 'dark' ? '#2a2a2a' : '#f1f1f1',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.type === 'dark' ? '#555' : '#888',
      borderRadius: '4px',
      '&:hover': {
        background: theme.palette.type === 'dark' ? '#777' : '#555',
      },
    },
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#f5f5f5',
    borderTop: theme.palette.type === 'dark' ? '1px solid #444' : '1px solid #e0e0e0',
    flexShrink: 0,
    gap: theme.spacing(1),
  },
  formControl: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.type === 'dark' ? '#1e1e1e' : '#ffffff',
      '&:hover': {
        backgroundColor: theme.palette.type === 'dark' ? '#2a2a2a' : '#f9f9f9',
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.type === 'dark' ? '#2a2a2a' : '#ffffff',
      },
    },
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.type === 'dark' ? '#1e1e1e' : '#ffffff',
      '&:hover': {
        backgroundColor: theme.palette.type === 'dark' ? '#2a2a2a' : '#f9f9f9',
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.type === 'dark' ? '#2a2a2a' : '#ffffff',
      },
    },
  },
  radioGroup: {
    marginTop: theme.spacing(1),
    '& .MuiFormControlLabel-root': {
      marginBottom: theme.spacing(0.5),
      '& .MuiRadio-root': {
        color: theme.palette.primary.main,
      },
    },
  },
  buttonPrimary: {
    background: theme.palette.type === 'dark' 
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
    fontWeight: 600,
    padding: theme.spacing(1, 3),
    boxShadow: '0 4px 15px 0 rgba(102, 126, 234, 0.4)',
    '&:hover': {
      background: theme.palette.type === 'dark'
        ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
        : 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
      boxShadow: '0 6px 20px 0 rgba(102, 126, 234, 0.5)',
      transform: 'translateY(-1px)',
    },
    '&:disabled': {
      background: theme.palette.type === 'dark' ? '#444' : '#ccc',
      color: theme.palette.type === 'dark' ? '#888' : '#666',
      boxShadow: 'none',
    },
    transition: 'all 0.3s ease',
  },
  buttonSecondary: {
    color: theme.palette.type === 'dark' ? '#fff' : theme.palette.primary.main,
    fontWeight: 500,
    padding: theme.spacing(1, 3),
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(102, 126, 234, 0.1)',
    },
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
  },
  formLabel: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.type === 'dark' ? '#fff' : theme.palette.text.primary,
  },
}));

const FinalizacaoVendaModal = ({ open, onClose, ticket, onFinalizar }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const colorModeContext = useContext(ColorModeContext);
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [vendaConcluida, setVendaConcluida] = useState(true);
  const [valorVenda, setValorVenda] = useState("");
  const [motivoNaoVenda, setMotivoNaoVenda] = useState("");
  const [motivoFinalizacao, setMotivoFinalizacao] = useState("");
  const [motivosFinalizacao, setMotivosFinalizacao] = useState([]);
  const [informarValorVenda, setInformarValorVenda] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [ticketDataToFinalize, setTicketDataToFinalize] = useState(null);

  const handleClose = () => {
    onClose();
    setVendaConcluida(true);
    setValorVenda("");
    setMotivoNaoVenda("");
    setMotivoFinalizacao("");
  };

  useEffect(() => {
    if (open) {
      setLoadingConfig(true);
      Promise.all([fetchMotivosFinalizacao(), fetchCompanySettings()]).then(
        () => setLoadingConfig(false)
      );
    }
  }, [open]);

  const fetchMotivosFinalizacao = async () => {
    try {
      const { data } = await api.get("/ticketFinalizationReasons");
      setMotivosFinalizacao(data);
    } catch (err) {
      console.error("Erro ao buscar motivos de finalização:", err);
    }
  };

  const fetchCompanySettings = async () => {
    try {
      const { data } = await api.get("/companySettings");
      setInformarValorVenda(data.informarValorVenda || false);
    } catch (err) {
      console.error("Erro ao buscar configurações da empresa:", err);
    }
  };

  const handleSubmit = async () => {
    if (informarValorVenda) {
      // Check ativado: lógica antiga
      if (vendaConcluida) {
        if (!valorVenda) {
          toast.error(i18n.t("finalizacaoVendaModal.errors.saleValueRequired"));
          return;
        }
      } else {
        if (!motivoNaoVenda) {
          toast.error(i18n.t("finalizacaoVendaModal.errors.noSaleReasonRequired"));
          return;
        }
      }
    } else {
      // Check desativado: só motivo de finalização
      if (!motivoFinalizacao) {
        toast.error(i18n.t("finalizacaoVendaModal.errors.finalizationReasonRequired"));
        return;
      }
    }

    // Em vez de finalizar aqui, apenas passa os dados para o pai
    const ticketData = {
      status: "closed",
      userId: user?.id || null,
      // sendFarewellMessage e finalizacaoMessage serão definidos no modal seguinte
    };

    if (informarValorVenda) {
      ticketData.finalizadoComVenda = vendaConcluida;
      if (vendaConcluida) {
        ticketData.valorVenda = parseFloat(valorVenda);
        ticketData.motivoNaoVenda = null;
        ticketData.motivoFinalizacao = null;
      } else {
        ticketData.valorVenda = null;
        ticketData.motivoNaoVenda = motivoNaoVenda;
        ticketData.motivoFinalizacao = null;
      }
    } else {
      ticketData.finalizadoComVenda = null;
      ticketData.valorVenda = null;
      ticketData.motivoNaoVenda = null;
      ticketData.motivoFinalizacao = motivoFinalizacao;
    }

    // Chama o callback passando os dados
    if (onFinalizar) onFinalizar(ticketData);
    handleClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        style: {
          backgroundColor: theme.palette.type === 'dark' ? '#1e1e1e' : '#ffffff',
          borderRadius: isMobile ? 0 : theme.shape.borderRadius,
        }
      }}
    >
      <DialogTitle className={classes.dialogTitle} style={createGradientStyles(theme).gradientBackground}>
        {i18n.t("finalizacaoVendaModal.title")}
      </DialogTitle>
      {loadingConfig ? (
        <DialogContent className={classes.dialogContent}>
          <Box className={classes.loadingContainer}>
            <CircularProgress size={40} />
            <Typography variant="body1" color="textSecondary">
              {i18n.t("finalizacaoVendaModal.loadingSettings")}
            </Typography>
          </Box>
        </DialogContent>
      ) : (
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={3}>
            {informarValorVenda ? (
              <>
                <Grid item xs={12}>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend" className={classes.formLabel}>
                      {i18n.t("finalizacaoVendaModal.saleCompletedQuestion")}
                    </FormLabel>
                    <RadioGroup
                      value={vendaConcluida}
                      onChange={(e) =>
                        setVendaConcluida(e.target.value === "true")
                      }
                      className={classes.radioGroup}
                    >
                      <FormControlLabel
                        value={true}
                        control={<Radio color="primary" />}
                        label={i18n.t("finalizacaoVendaModal.saleCompletedYes")}
                      />
                      <FormControlLabel
                        value={false}
                        control={<Radio color="primary" />}
                        label={i18n.t("finalizacaoVendaModal.saleCompletedNo")}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                {vendaConcluida ? (
                  <Grid item xs={12}>
                    <TextField
                      label={i18n.t("finalizacaoVendaModal.saleValue")}
                      type="number"
                      value={valorVenda}
                      onChange={(e) => setValorVenda(e.target.value)}
                      fullWidth
                      variant="outlined"
                      margin="dense"
                      className={classes.textField}
                      inputProps={{
                        step: "0.01",
                        min: "0",
                      }}
                      placeholder="0,00"
                    />
                  </Grid>
                ) : (
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      variant="outlined"
                      margin="dense"
                      required
                      className={classes.formControl}
                    >
                      <InputLabel>{i18n.t("finalizacaoVendaModal.noSaleReason")}</InputLabel>
                      <Select
                        value={motivoNaoVenda}
                        onChange={(e) => setMotivoNaoVenda(e.target.value)}
                        label={i18n.t("finalizacaoVendaModal.noSaleReason")}
                      >
                        <MenuItem value="">
                          <em>{i18n.t("finalizacaoVendaModal.selectReason")}</em>
                        </MenuItem>
                        {motivosFinalizacao.map((motivo) => (
                          <MenuItem key={motivo.id} value={motivo.name}>
                            {motivo.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </>
            ) : (
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  required
                  className={classes.formControl}
                >
                  <InputLabel>{i18n.t("finalizacaoVendaModal.finalizationReason")}</InputLabel>
                  <Select
                    value={motivoFinalizacao}
                    onChange={(e) => setMotivoFinalizacao(e.target.value)}
                    label={i18n.t("finalizacaoVendaModal.finalizationReason")}
                  >
                    <MenuItem value="">
                      <em>{i18n.t("finalizacaoVendaModal.selectReason")}</em>
                    </MenuItem>
                    {motivosFinalizacao.map((motivo) => (
                      <MenuItem key={motivo.id} value={motivo.name}>
                        {motivo.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
      )}
      <DialogActions className={classes.dialogActions}>
        <Button 
          onClick={handleClose} 
          className={classes.buttonSecondary}
          disabled={loading || loadingConfig}
          variant="text"
        >
          {i18n.t("common.buttons.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          className={classes.buttonPrimary}
          variant="contained"
          disabled={loading || loadingConfig}
        >
          {loading ? (
            <>
              <CircularProgress size={16} style={{ marginRight: 8, color: '#fff' }} />
              {i18n.t("finalizacaoVendaModal.finishing")}
            </>
          ) : (
            i18n.t("finalizacaoVendaModal.finishService")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FinalizacaoVendaModal;
